'use client';
import { FC, Suspense, useCallback, useEffect } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useRouter } from 'next/navigation';

import { Logo, Svg, Text } from '@/components';
import SearchBar from '@/components/Inputs/SearchBar';
import SectionCart from '@/features/Cart/sections/SectionCart';
import CartSidebarLayout from '@/features/Cart/layouts/CartSidebarLayout';
import useToggle from '@/hooks/useToggle';
import { useUser } from '@/context/userContext';
import {
  UserIcon,
  CartIcon,
  FrameIcon,
  HeartOutlineIcon,
  MenuIcon,
  LocationPinIcon,
  ChevronDownIcon,
} from '@/icons';
import { HEADER_FOOTER_TYPES } from '@/constants';

import Container from '../Container';
import NavigationLinks from './components/NavigationLinks';
import Menu from '../Menu';
import SearchDropdown from './components/SearchDropdown';
import { useCart } from '@/context/cartContext';
import { PATHNAMES } from '@/features/Checkout/constants/delivery';
import HoverDropdown from './components/HoverDropdown';
import Ticker from '@/layouts/Ticker';
import { useWishlist } from '@/context/wishlistContext';
import TooltipToast from '../Toast/TooltipToast';
import { useHeaderContext } from '@/context/headerContext';
import { storage } from '@/config';
import LocationFlow from '@/features/Location/components/LocationFlow';
import { useLocation } from '@/context/locationContext';
import SearchManually from '@/features/Location/components/SearchManually';
import DeliveryDetails from '@/features/Location/components/DeliveryDetails';
import PickupStore from '@/features/Location/components/PickupStore';
import NonServiceablePopup from '@/features/Location/components/NonServiceablePopup';
import StoreChangeModal from '@/features/Cart/components/StoreChangeModal';
import StorePickupIcon from '@/icons/StorePickupIcon';

export type PageType = 'global' | 'checkout';

interface PropTypes {
  type: PageType | string;
}

interface UserActionProps {
  onClose: (...args: any) => void;
}

const FrameText = () => {
  return (
    <div className='flex items-center justify-end gap-1'>
      <Svg fill='none' className='stroke-0 stroke-[#4D7225]'>
        <FrameIcon />
      </Svg>
      <Text className='text-[#4D7225] text-xs font-semibold px-0'>
        100% SECURE
      </Text>
    </div>
  );
};

const Header: FC<PropTypes> = ({ type = HEADER_FOOTER_TYPES.global }) => {
  // Constants
  const router: any = useRouter();
  // Context
  const {
    userDetails,
    setUserDetails,
    setShowLoginModal,
    setLoginRedirect,
    setLoginModalMessage,
    setLoginEventMetadata,
  } = useUser();

  const {
    cartItemsCount,
    isCartSidebarOpen,
    toggleCartSidebar,
    cartToast,
    setIsProductPage,
  } = useCart();
  const { wishlistItems } = useWishlist();
  const {
    pathNameArr,
    shouldColorChange,
    showHoverDropdown,
    categoriesToShowOnHover,
    isNavLinksHidden,
    searchBarRef,
    toggleIsSearchDrawerOpen,
    toggleHoverDropdown,
    setQuery,
  } = useHeaderContext();

  const {
    showLocationModal,
    setShowLocationModal,
    showSearchManually,
    setShowSearchManually,
    showPickupStore,
    setShowPickupStore,
    showNonServiceablePopup,
    setShowNonServiceablePopup,
    showStoreChangeModal,
    setShowStoreChangeModal,
    selectedStore,
    setShowDeliveryModal,
    showDeliveryModal,
    headerLocationTitle,
    deliveryType,
  } = useLocation();

  // Toggle States
  const { wishlistToast } = useWishlist();
  const { locationToast } = useLocation();

  const { value: isMenuOpen, toggleValue: toggleMenu } = useToggle(false);
  // Other States

  const headerClassNames = twMerge(
    'w-full transition-none bg-transparent text-white',
    shouldColorChange && 'bg-white text-black',
    showHoverDropdown &&
      categoriesToShowOnHover &&
      categoriesToShowOnHover.length > 0 &&
      'border-b border-b-[#EDEAE9]'
  );

  const svgClassNames = twMerge(
    'header_svgs fill-none stroke-2 transition-colors',
    shouldColorChange ? 'stroke-black' : 'stroke-white'
  );

  useEffect(() => {
    setIsProductPage(false);
  }, []);

  const onAccount = (href = '/account/') => {
    const token = storage.get('token');
    if (token) {
      router.push(href);
      return;
    } else {
      if (userDetails?.id) {
        setUserDetails(null);
      }
      setLoginModalMessage(
        href === '/account/'
          ? 'Login using your mobile number to embark on an unforgettable food journey'
          : 'Log in to save and savour your favourite food tales!'
      );
      setLoginEventMetadata({
        source:
          href === '/account/' ? 'Header Account Login' : 'Header Shelf Login',
      });
      setLoginRedirect(href);
      setShowLoginModal(true);
    }
  };

  useEffect(() => {
    sessionStorage.removeItem('shipping_address');
    sessionStorage.removeItem('billing_address');
    sessionStorage.removeItem('selectedAddressId');
    sessionStorage.removeItem('selected_delivery_slot');
    sessionStorage.removeItem('shipping_amount');
    sessionStorage.removeItem('paymentMethods');
  }, []);

  const openCartSidebar = () => toggleCartSidebar(true);

  const handleDropDownState = useCallback(
    (nullifyQuery = false, callback: () => void = () => {}) => {
      toggleIsSearchDrawerOpen(false);
      toggleHoverDropdown(false);
      if (nullifyQuery) {
        setQuery('');
      }
      if (searchBarRef?.current) {
        searchBarRef.current?.blur();
      }
      if (callback) {
        callback();
      }
    },
    [toggleIsSearchDrawerOpen, toggleHoverDropdown, setQuery, searchBarRef]
  );

  const UserActions: FC<UserActionProps> = ({ onClose }) => {
    return (
      <div
        aria-label='user-actions'
        className='flex items-center justify-end space-x-4 max-[465px]:w-full max-[465px]:space-x-2 z-21'>
        <button
          type='button'
          onClick={() => {
            setShowDeliveryModal(true);
          }}
          className={twMerge(
            'relative flex min-w-0 items-center justify-center rounded-[3.75rem] bg-black/40 px-1 py-1 outline-hidden backdrop-blur-xs max-md:pl-2 md:px-3 md:py-2',
            shouldColorChange ? 'bg-black/5' : 'bg-black/40'
          )}
          title='Location'>
          <div className='flex w-full flex-row items-center justify-center'>
            {deliveryType === 'pickup' &&
            headerLocationTitle !== 'Fetching Location' ? (
              <Svg
                viewBox='0 0 17 17'
                fill={shouldColorChange ? '#38383A' : '#FFF'}
                className={twMerge(
                  'h-[18px] w-[18px] stroke-black stroke-[0.3] mr-1',
                  shouldColorChange ? 'stroke-black' : 'stroke-white'
                )}>
                <StorePickupIcon />
              </Svg>
            ) : (
              <Svg
                className={twMerge(svgClassNames, 'mt-[5px] h-6 w-6')}
                strokeWidth='1.5'>
                <LocationPinIcon />
              </Svg>
            )}

            <div className='block min-w-0 max-w-48 truncate px-0 pr-1.5 text-[12px] max-[465px]:max-w-16 max-[355px]:max-w-12'>
              {headerLocationTitle}
            </div>
            <Svg
              className={twMerge(svgClassNames, 'mt-[5px] h-5 w-5')}
              strokeWidth='1.5'>
              <ChevronDownIcon />
            </Svg>
          </div>
          {!!locationToast && (
            <TooltipToast
              message={locationToast.message}
              deliveryType={locationToast.deliveryType}
            />
          )}
        </button>
        <button
          type='button'
          onClick={() => {
            onClose();
            onAccount('/account/shelf/');
          }}
          className='relative hidden min-[530px]:block'
          title='Shelf'>
          <Svg
            className={twMerge(
              svgClassNames
              // animateHeart && 'fill-fig stroke-fig'
            )}
            viewBox='0 0 16 16'
            strokeWidth='1.5'>
            <HeartOutlineIcon />
          </Svg>
          {wishlistItems && wishlistItems?.length > 0 && (
            <div className='absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-fig'>
              <div
                className={twMerge(
                  'font-gilroy text-white',
                  wishlistItems.length.toString().length > 1
                    ? 'text-xs'
                    : 'text-sm'
                )}>
                {wishlistItems?.length}
              </div>
            </div>
          )}
          {!!wishlistToast && (
            <TooltipToast
              message={wishlistToast.message}
              type={wishlistToast.type}
            />
          )}
        </button>
        <button
          onClick={() => {
            onClose();
            onAccount(userDetails ? '/account/' : '/');
          }}
          className='hidden min-[500px]:block'
          title={userDetails ? 'Account' : 'Login'}>
          <Svg className={svgClassNames}>
            <UserIcon />
          </Svg>
        </button>
        <button
          className='relative'
          onClick={() => {
            handleDropDownState(false, openCartSidebar);
          }}
          id='cart-icon'
          title='Cart'>
          <Svg className={svgClassNames}>
            <CartIcon />
          </Svg>
          {cartItemsCount && cartItemsCount > 0 ? (
            <div className='absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-fig'>
              <div
                className={twMerge(
                  'font-gilroy text-white',
                  cartItemsCount > 9 ? 'text-xs' : 'text-sm'
                )}>
                {cartItemsCount}
              </div>
            </div>
          ) : null}
          {!!cartToast && (
            <TooltipToast message={cartToast.message} type={cartToast.type} />
          )}
        </button>
      </div>
    );
  };

  return (
    <>
      <div
        className={
          type === HEADER_FOOTER_TYPES.hidden
            ? 'hidden'
            : 'sticky top-0 w-full z-20'
        }>
        <div className='relative flex flex-col w-full'>
          {type === HEADER_FOOTER_TYPES.global && (
            <Suspense fallback={null}>
              <Ticker />
            </Suspense>
          )}
          <nav id='header' className={headerClassNames}>
            <Container className='max-lg:flex max-lg:flex-col max-lg:justify-start'>
              <div
                className={twJoin(
                  `flex w-full items-center justify-between py-4 max-lg:h-auto max-md:px-3 lg:gap-x-4`
                )}>
                <div className='flex items-center justify-start gap-x-4 max-[465px]:gap-x-2'>
                  {type === HEADER_FOOTER_TYPES.global && (
                    <>
                      <button
                        className='md:hidden'
                        title='Menu'
                        onClick={() => handleDropDownState(false, toggleMenu)}>
                        <div className=' w-5 h-5'>
                          <Svg
                            fill='none'
                            className={
                              shouldColorChange
                                ? 'fill-black stroke-[#38383A] stroke-1'
                                : 'fill-white stroke-white stroke-1'
                            }>
                            <MenuIcon />
                          </Svg>
                        </div>
                      </button>
                    </>
                  )}
                  <Logo
                    rootClasses='md:w-44'
                    color={shouldColorChange ? 'black' : 'white'}
                    closeSearchDropdown={() => handleDropDownState(true)}
                  />
                </div>
                {type === HEADER_FOOTER_TYPES.global && (
                  <>
                    <div className='flex w-full items-center justify-center max-lg:hidden'>
                      <SearchBar headerClass={headerClassNames} />
                    </div>
                    <UserActions onClose={() => handleDropDownState(false)} />
                  </>
                )}
                {type === HEADER_FOOTER_TYPES.checkout && (
                  <>
                    <FrameText />
                  </>
                )}
              </div>
              {type === HEADER_FOOTER_TYPES.global && (
                <div className='lg:hidden'>
                  <SearchBar headerClass={headerClassNames} />
                </div>
              )}
              {type === HEADER_FOOTER_TYPES.global && (
                <div
                  className={twMerge(
                    'transition-nav-link opacity-100 max-md:hidden md:max-h-full md:translate-y-0',
                    isNavLinksHidden &&
                      'md:z-[-5] md:max-h-0 md:-translate-y-6 md:opacity-0'
                  )}>
                  <div
                    className={twMerge(
                      'justify-center space-x-5 pb-5 max-md:flex md:max-h-full',
                      isNavLinksHidden &&
                        !showHoverDropdown &&
                        'md:max-h-0 md:pb-0'
                    )}>
                    <NavigationLinks
                      menuOpen={isMenuOpen}
                      showHoverDropdown={showHoverDropdown}
                    />
                  </div>
                </div>
              )}
            </Container>
          </nav>
          {categoriesToShowOnHover && categoriesToShowOnHover.length > 0 && (
            <HoverDropdown />
          )}
          <SearchDropdown />
        </div>
      </div>
      {pathNameArr !== PATHNAMES.CHECKOUT && (
        <CartSidebarLayout
          onClose={toggleCartSidebar}
          show={isCartSidebarOpen}
          id='cart'>
          {isCartSidebarOpen && <SectionCart />}
        </CartSidebarLayout>
      )}
      <Menu id='menu' onClose={toggleMenu} show={isMenuOpen} />
      {showDeliveryModal && (
        <DeliveryDetails
          show={showDeliveryModal}
          onClose={() => setShowDeliveryModal(false)}
        />
      )}
      {showLocationModal && !showSearchManually && (
        <LocationFlow
          show={showLocationModal}
          onClose={() => setShowLocationModal(false)}
        />
      )}
      {showSearchManually && !showLocationModal && (
        <SearchManually
          show={showSearchManually}
          onClose={() => setShowSearchManually(false)}
        />
      )}
      {showPickupStore && (
        <PickupStore
          show={showPickupStore}
          onClose={() => setShowPickupStore(false)}
        />
      )}
      {showNonServiceablePopup && (
        <NonServiceablePopup
          show={showNonServiceablePopup}
          onClose={() => setShowNonServiceablePopup(false)}
        />
      )}
      {showStoreChangeModal && (
        <StoreChangeModal
          show={showStoreChangeModal}
          onClose={() => setShowStoreChangeModal(false)}
          selectedStore={selectedStore}
        />
      )}
    </>
  );
};

export default Header;
